/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { Router } from "@angular/router";


import { AuthenticationService } from './authentication.service';
import { BaseService } from "./base-service";

import { Antenna } from "../models/antenna";
import { AntennaInstance } from "../models/antenna-instance";
import { DriverObject } from '../models/driver-object';
import { DriverObjectInstance } from '../models/driver-object-instance';
import { EmGroup } from '../models/em-group';
import { GroupType } from '../models/group-type';
import { Location } from '../models/location';
import { LocationInstance } from '../models/location-instance';
import { ObjectDefinition } from '../models/object-definition';
import { ObjectInstance } from '../models/object-instance';
import { Reader } from "../models/reader";
import { ReaderInstance } from "../models/reader-instance";
import { ServerObject } from '../models/server-object';
import { ServerObjectInstance } from '../models/server-object-instance';
import { Service } from '../models/service';
import { ServiceInstance } from '../models/service-instance';
import { TagDefinition } from '../models/tag-definition';
import { TagInstance } from '../models/tag-instance';
import { Zone } from '../models/zone';
import { ZoneInstance } from '../models/zone-instance';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';

@Injectable()
export class GroupService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/groups/`;
  }


  getAllGroups(): Promise<EmGroup[]> {
    const url = this.getUrl();
    return this.mRestService.get<EmGroup[]>(url);
  }

  getGroupsFiltered(start: number, size: number, order: string, filterString: string): Promise<EmGroup[]> {
    const url = this.getUrl() + `filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<EmGroup[]>(url);
  } 

  getAllGroupsForGroupType(groupType: GroupType): Promise<EmGroup[]> {
    const url = this.getUrl() + `type/${groupType}`;
    return this.mRestService.get<EmGroup[]>(url);
  }

  getAllObjectsInGroup(groupId: number): Promise<ObjectDefinition[]> {
    const url = this.getUrl() + `object/${groupId}`;
    return this.mRestService.get<ObjectDefinition[]>(url);
  }

  getAllObjectInstancesInGroup(groupId: number): Promise<ObjectInstance[]> {
    const url = this.getUrl() + `object/instances/${groupId}`;
    return this.mRestService.get<ObjectInstance[]>(url);
  }

  getAllReadersInGroup(groupId: number): Promise<Reader[]> {
    const url = this.getUrl() + `reader/${groupId}`;
    return this.mRestService.get<Reader[]>(url);
  }

  getAllReaderInstancesInGroup(groupId: number): Promise<ReaderInstance[]> {
    const url = this.getUrl() + `reader/instances/${groupId}`;
    return this.mRestService.get<ReaderInstance[]>(url);
  }

  getAllAntennasInGroup(groupId: number): Promise<Antenna[]> {
    const url = this.getUrl() + `antenna/${groupId}`;
    return this.mRestService.get<Antenna[]>(url);
  }

  getAllAntennaInstancesInGroup(groupId: number): Promise<AntennaInstance[]> {
    const url = this.getUrl() + `antenna/instances/${groupId}`;
    return this.mRestService.get<AntennaInstance[]>(url);
  }

  getAllTagDefinitionsInGroup(groupId: number): Promise<TagDefinition[]> {
    const url = this.getUrl() + `asset/{groupId}`;
    return this.mRestService.get<TagDefinition[]>(url);
  }

  getAllTagInstancesInGroup(groupId: number): Promise<TagInstance[]> {
    const url = this.getUrl() + `asset/tags/${groupId}`;
    return this.mRestService.get<TagInstance[]>(url);
  }

  getAllDriverObjectsInGroup(groupId: number): Promise<DriverObject[]> {
    const url = this.getUrl() + `driver-object/${groupId}`;
    return this.mRestService.get<DriverObject[]>(url);
  }

  getAllDriverObjectInstancesInGroup(groupId: number): Promise<DriverObjectInstance[]> {
    const url = this.getUrl() + `driver-object/instances/${groupId}`;
    return this.mRestService.get<DriverObjectInstance[]>(url);
  }

  getAllLocationsInGroup(groupId: number): Promise<Location[]> {
    const url = this.getUrl() + `location/${groupId}`;
    return this.mRestService.get<Location[]>(url);
  }

  getAllLocationInstancesInGroup(groupId: number): Promise<LocationInstance[]> {
    const url = this.getUrl() + `location/instances/${groupId}`;
    return this.mRestService.get<LocationInstance[]>(url);
  }

  getAllServerObjectsInGroup(groupId: number): Promise<ServerObject[]> {
    const url = this.getUrl() + `server-object/${groupId}`;
    return this.mRestService.get<ServerObject[]>(url);
  }

  getAllServerObjectInstancesInGroup(groupId: number): Promise<ServerObjectInstance[]> {
    const url = this.getUrl() + `server-object/instances/${groupId}`;
    return this.mRestService.get<ServerObjectInstance[]>(url);
  }

  getAllServicesInGroup(groupId: number): Promise<Service[]> {
    const url = this.getUrl() + `service/${groupId}`;
    return this.mRestService.get<Service[]>(url);
  }

  getAllServiceInstancesInGroup(groupId: number): Promise<ServiceInstance[]> {
    const url = this.getUrl() + `service/instances/${groupId}`;
    return this.mRestService.get<ServiceInstance[]>(url);
  }

  getAllZonesInGroup(groupId: number): Promise<Zone[]> {
    const url = this.getUrl() + `readzone/${groupId}`;
    return this.mRestService.get<Zone[]>(url);
  }

  getAllZoneInstancesInGroup(groupId: number): Promise<ZoneInstance[]> {
    const url = this.getUrl() + `readzone/instances/${groupId}`;
    return this.mRestService.get<ZoneInstance[]>(url);
  }

  createGroup(group: EmGroup): Promise<EmGroup> {
    const url = this.getUrl();
    return this.mRestService.post<EmGroup>(url, group);
  }

  deleteGroups(groupIds: number[]): Promise<void> {
    const url = this.getUrl();
    return this.mRestService.delete(url, groupIds);
  }

  updateGroup(group: EmGroup): Promise<EmGroup> {
    const url = this.getUrl();
    return this.mRestService.put<EmGroup>(url, group);
  }

  addItemsToGroup(groupId: number, itemIds: number[]): Promise<void> {
    const url = this.getUrl() + `${groupId}`;
    return this.mRestService.put(url, itemIds);
  }

  removeItemsFromGroup(groupId: number, itemIds: number[]): Promise<void> {
    const url = this.getUrl() + `${groupId}/items`;
    return this.mRestService.delete(url, itemIds);
  }

}
